/* eslint-disable react/jsx-wrap-multilines */
import React from 'react';
import update from 'react-addons-update';
import Checkbox from '@material-ui/core/Checkbox';
import Radio from '@material-ui/core/Radio';
import { TextField, InputAdornment } from '@material-ui/core';

import { useClickAway } from 'react-use';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';

import { createStyles, makeStyles } from '@material-ui/core/styles';
import { colors } from '../../../theme/theme';
import { ReactComponent as SearchIcon } from '../../../assets/svg/icons/search-grey.svg';
import { ReactComponent as FilterIcon } from '../../../assets/svg/icons/filter.svg';
import { ReactComponent as CaretDownIcon } from '../../../assets/svg/icons/caret-down.svg';
import { ReactComponent as CloseIcon } from '../../../assets/svg/icons/close.svg';

const useStyles = makeStyles(
    createStyles({
        mainContainer: {
            padding: '18px 0px 5px 18px',
        },
        containerNew: {
            minWidth: '300px',
            maxHeight: '600px',
            paddingRight: '22px',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
                width: '0.3em'
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#4169e1',
                borderRadius: '10px'
            }
        },
        container: {
            padding: '5px 22px 5px 20px',
            minWidth: '250px'
        },
        row: {
            borderBottom: `1px solid ${colors.grey80}`,
            padding: '15px 0',
            '&:last-child': {
                borderBottom: 0
            }
        },
        textfieldContainer: {
            height: '66px',
            paddingRight: '22px',
            justifyContent: 'center'
        },
        endAdornment: {
            marginRight: '8px',
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%'
        },
        dropdown: {
            zIndex: 99
        },
        formField: {
            justifyContent: 'space-between',
            alignItems: 'start',
            margin: '0',
            width: '100%'
        },
        checkbox: {
            padding: '0'
        },
        radio: {
            padding: '0'
        }
    })
);

const Filter = ({ filterKey, filterItems, filterTitle, onUpdate, isRadio, showSearch, isGenAiDeliveryPlatformSelect }: FilterProps) => {
    const [state, setState] = React.useState({
        open: false
    });
    const [filterList, setFilterList] = React.useState<FilterCheckBoxItem[]>([]);
    const [filterValue, setFilterValue] = React.useState<string>('');

    const anchorRef = React.useRef<HTMLButtonElement>(null);
    const dropdownRef = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
        setFilterList(filterItems);
    }, [filterItems]);

    function handleToggle() {
        setState(prevState => ({ ...state, open: !prevState.open }));
    }

    useClickAway(dropdownRef, () => {
        setState({ ...state, open: false });
        setFilterValue('');
        setFilterList(filterItems);
    });

    const handleChange = (index: number, isSearch: boolean, itemId: string) => {
        let itemIndex = index;
        if (isSearch && filterValue) {
            itemIndex = filterItems.findIndex(x => x.name === itemId);
        }
        const checkedState = filterItems[itemIndex].checked;

        const updatedItems = update(filterItems, { [itemIndex]: { checked: { $set: !checkedState } } });

        onUpdate(updatedItems, filterKey);
    };

    const handleRadioChange = (index: number) => {
        const checkedState = filterItems[index].checked;

        filterItems.forEach(item => {
            item.checked = false;
        });

        const updatedItems = update(filterItems, { [index]: { checked: { $set: !checkedState } } });

        onUpdate(updatedItems, filterKey);
    };

    const { open } = state;
    const numberOfFilters = filterItems.filter(item => item.checked).length;
    const classes = useStyles(0);

    const onValueChange = (event: React.ChangeEvent) => {
        const { value } = event.currentTarget as HTMLInputElement;
        const regex = /^\s+/g;
        const newValue = value.replace(regex, '');
        const newList = filterItems.filter((item => item.name.toLowerCase().includes(newValue.toLowerCase())));
        setFilterValue(newValue);
        setFilterList(newList);
    };

    React.useEffect(() => {
        if (filterValue) {
            setFilterList(filterItems.filter(item => item.name.toLowerCase().includes(filterValue.toLowerCase())));
        }
    }, [filterItems]);

    const dataInputProps = {
        'data-testid': 'filter-dataset-search',
        'aria-label': 'filter dataset search'
    };

    return (
        <>
            <div className={`filter ${isGenAiDeliveryPlatformSelect ? 'search-input-integrated-select' : ''}`}>
                <button
                    aria-owns={open ? 'menu-list-grow' : undefined}
                    aria-haspopup="true"
                    onClick={handleToggle}
                    type="button"
                    className="filter__button button--select-control button--padding"
                    ref={anchorRef}
                >
                    {isGenAiDeliveryPlatformSelect && (
                        <FilterIcon className="filter__button--filter-icon" />
                    )}
                    {numberOfFilters > 0 && (
                        <span className="filter__button--text">{`${filterTitle} (${numberOfFilters})`}</span>
                    )}
                    {numberOfFilters === 0 && filterTitle}

                    {isGenAiDeliveryPlatformSelect && (
                        <CaretDownIcon className="filter__button--caret-icon" />
                    )}
                </button>
                <Popper
                    anchorEl={anchorRef.current}
                    className={classes.dropdown}
                    disablePortal
                    open={open}
                    placement="bottom-start"
                    transition
                >
                    {({ TransitionProps, placement }) => (
                        <Grow
                            {...TransitionProps}
                            style={{
                                transformOrigin: placement === 'bottom-start' ? 'center top' : 'center bottom'
                            }}
                        >
                            <Paper id="menu-list-grow" className="filter__dropdown" ref={dropdownRef}>
                                <div className={showSearch ? classes.mainContainer : ''}>
                                    {isGenAiDeliveryPlatformSelect && (
                                        <CloseIcon className="filter__close-icon" onClick={() => setState({ open: false })} />
                                    )}
                                    { showSearch && (
                                        <div className={classes.textfieldContainer}>
                                            <TextField
                                                id="filter-dataset-search"
                                                label="Search by..."
                                                autoComplete="off"
                                                onChange={onValueChange}
                                                value={filterValue}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end" className={classes.endAdornment}><SearchIcon /></InputAdornment>,
                                                    ...dataInputProps
                                                }}
                                                InputLabelProps={{ shrink: true }}
                                                className="filter__textfield"
                                            />
                                        </div>
                                    )}
                                    <div className={showSearch ? classes.containerNew : classes.container}>
                                        {filterList.map((item, index) => (
                                            <FormGroup row key={item.name} className={classes.row}>
                                                <FormControlLabel
                                                    key={item.name}
                                                    className={classes.formField}
                                                    control={
                                                        isRadio ? (
                                                            <Radio
                                                                key={item.name}
                                                                checked={item.checked}
                                                                onClick={() => handleRadioChange(index)}
                                                                value={item.name}
                                                                name={item.name}
                                                                className={classes.radio}
                                                                color="primary"
                                                            />
                                                        ) : (
                                                            <Checkbox
                                                                key={item.name}
                                                                checked={item.checked}
                                                                onChange={() => handleChange(index, showSearch || false, item.name)}
                                                                value={item.name}
                                                                name={item.name}
                                                                className={classes.checkbox}
                                                                color="primary"
                                                            />
                                                        )
                                                    }
                                                    label={item.name}
                                                    labelPlacement="start"
                                                />
                                            </FormGroup>
                                        ))}
                                    </div>
                                </div>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
            </div>
        </>
    );
};
export default Filter;
